if (!customElements.get('quick-add-modal')) {
  const previewScriptUrl = document.currentScript?.dataset.previewScript;
  let previewScriptRequest;
  const dynamicPreviewCache = new Map();

  const waitForPreviewStyles = () => {
    const stylesheet = document.getElementById('PerfumiarzQuickAddStyles');
    if (!stylesheet) return Promise.resolve();
    if (stylesheet.sheet) {
      stylesheet.media = 'all';
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      const finish = (error) => {
        clearTimeout(timeout);
        stylesheet.removeEventListener('load', loaded);
        stylesheet.removeEventListener('error', failed);
        if (error) reject(error);
        else resolve();
      };
      const loaded = () => { stylesheet.media = 'all'; finish(); };
      const failed = () => finish(new Error('Unable to load quick preview styles'));
      const timeout = setTimeout(failed, 10000);
      stylesheet.addEventListener('load', loaded, { once: true });
      stylesheet.addEventListener('error', failed, { once: true });
    });
  };

  const loadPreviewScript = () => {
    if (window.perfumiarzQuickPreview) return Promise.resolve();
    if (previewScriptRequest) return previewScriptRequest;

    previewScriptRequest = new Promise((resolve, reject) => {
      if (!previewScriptUrl) {
        reject(new Error('Missing quick preview script URL'));
        return;
      }
      const script = document.createElement('script');
      script.src = previewScriptUrl;
      script.onload = resolve;
      script.onerror = () => {
        script.remove();
        reject(new Error('Unable to load quick preview'));
      };
      document.head.appendChild(script);
    }).catch((error) => {
      previewScriptRequest = null;
      throw error;
    });
    return previewScriptRequest;
  };

  const buildDynamicPreviewUrl = (productUrl, endpoint) => {
    const requestUrl = new URL(productUrl, window.location.origin);
    const currentParams = new URLSearchParams(window.location.search);
    const previewThemeId = currentParams.get('preview_theme_id');

    requestUrl.search = '';
    requestUrl.searchParams.set('section_id', endpoint);
    if (previewThemeId) requestUrl.searchParams.set('preview_theme_id', previewThemeId);
    return requestUrl.toString();
  };

  const requestDynamicPreview = (productUrl, endpoint) => {
    const requestUrl = buildDynamicPreviewUrl(productUrl, endpoint);
    if (dynamicPreviewCache.has(requestUrl)) return dynamicPreviewCache.get(requestUrl);

    const request = fetch(requestUrl, {
      credentials: 'same-origin',
      headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Quick preview request failed with status ${response.status}`);
        return response.text();
      })
      .then((responseText) => {
        const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceModal = responseHTML.querySelector('quick-add-modal[data-static-content="true"]');
        const sourceContent = sourceModal?.querySelector('.quick-add-modal__content-info');
        const sourceDialog = sourceModal?.querySelector('[role="dialog"]');
        if (!sourceContent || !sourceDialog) throw new Error('Quick preview response was incomplete');

        return {
          content: sourceContent.innerHTML,
          label: sourceDialog.getAttribute('aria-label') || '',
        };
      })
      .catch((error) => {
        dynamicPreviewCache.delete(requestUrl);
        throw error;
      });

    dynamicPreviewCache.set(requestUrl, request);
    if (dynamicPreviewCache.size > 5) dynamicPreviewCache.delete(dynamicPreviewCache.keys().next().value);
    return request;
  };

  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this.handleVisualViewportChange = this.updateVisualViewport.bind(this);

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      updateVisualViewport() {
        if (!this.classList.contains('perfumiarz-products__quick-add-modal')) return;

        const viewport = window.visualViewport;
        const viewportHeight = viewport?.height || window.innerHeight;
        const viewportTop = viewport?.offsetTop || 0;
        this.style.setProperty('--perfumiarz-modal-viewport-height', `${viewportHeight}px`);
        this.style.setProperty('--perfumiarz-modal-viewport-top', `${viewportTop}px`);
      }

      startVisualViewportTracking() {
        if (!this.classList.contains('perfumiarz-products__quick-add-modal')) return;

        this.updateVisualViewport();
        window.visualViewport?.addEventListener('resize', this.handleVisualViewportChange, { passive: true });
        window.visualViewport?.addEventListener('scroll', this.handleVisualViewportChange, { passive: true });
        window.addEventListener('resize', this.handleVisualViewportChange, { passive: true });
      }

      stopVisualViewportTracking() {
        window.visualViewport?.removeEventListener('resize', this.handleVisualViewportChange);
        window.visualViewport?.removeEventListener('scroll', this.handleVisualViewportChange);
        window.removeEventListener('resize', this.handleVisualViewportChange);
      }

      hide(preventFocus = false) {
        this.stopVisualViewportTracking();
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        if (!this.hasAttribute('data-static-content')) {
          this.modalContent.innerHTML = '';
        }

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      async show(opener) {
        if (this.hasAttribute('data-dynamic-content')) {
          if (this.previewLoading) return;

          const productUrl = opener.getAttribute('data-product-url');
          const endpoint = this.getAttribute('data-preview-endpoint');
          if (!productUrl || !endpoint) {
            if (productUrl) window.location.assign(productUrl);
            return;
          }

          this.previewLoading = true;
          opener.setAttribute('aria-busy', 'true');
          opener.classList.add('loading');
          const spinner = opener.querySelector('.loading__spinner');
          spinner?.classList.remove('hidden');

          try {
            const [{ content, label }] = await Promise.all([
              requestDynamicPreview(productUrl, endpoint),
              loadPreviewScript(),
              waitForPreviewStyles(),
            ]);

            this.modalContent.innerHTML = content;
            this.modalContent.scrollTop = 0;
            const dialog = this.querySelector('[role="dialog"]');
            if (label && dialog) dialog.setAttribute('aria-label', label);

            this.startVisualViewportTracking();
            super.show(opener);
            window.perfumiarzQuickPreview.open(this.querySelector('[data-preview-root]'));
            document.dispatchEvent(new CustomEvent('quick-add-modal:open'));
          } catch (error) {
            console.error(error);
            window.location.assign(productUrl);
          } finally {
            this.previewLoading = false;
            opener.removeAttribute('aria-busy');
            opener.classList.remove('loading');
            spinner?.classList.add('hidden');
          }
          return;
        }

        if (this.hasAttribute('data-static-content')) {
          if (this.previewLoading) return;
          this.previewLoading = true;
          opener.setAttribute('aria-busy', 'true');
          opener.classList.add('loading');
          const spinner = opener.querySelector('.loading__spinner');
          spinner?.classList.remove('hidden');
          try {
            await Promise.all([loadPreviewScript(), waitForPreviewStyles()]);
            if (!opener.isConnected) return;
            this.modalContent.scrollTop = 0;
            this.startVisualViewportTracking();
            super.show(opener);
            window.perfumiarzQuickPreview.open(this.querySelector('[data-preview-root]'));
          } catch (error) {
            console.error(error);
            const productUrl = opener.getAttribute('data-product-url');
            if (productUrl) window.location.assign(productUrl);
          } finally {
            this.previewLoading = false;
            opener.removeAttribute('aria-busy');
            opener.classList.remove('loading');
            spinner?.classList.add('hidden');
          }
          return;
        }

        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.modalContent.scrollTop = 0;
            this.startVisualViewportTracking();
            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
